import { DocumentType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolFormattedMessage } from '@synergy/components-next';
import { ReactElement } from 'react';

import { PaloStatusChangeDialog } from './palo-status-change-dialog';
import { useCompletionWarnings } from './use-completion-warnings';

export interface PaloCompleteDocumentDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    titleId: string;
    /** The question, with `{number}` for the document's number. */
    contentMessageId: string;
    resourceNumber?: string;
    /** Overrides the "Abschließen" on the confirming button. */
    confirmId?: string;
    /** The tax rates and the invoice address, for the tax check. */
    taxes?: number[];
    invoiceLocation?: string;
    /** The document the remark belongs to; without it no remark is offered. */
    documentType?: DocumentType;
    documentId?: number;
}

/**
 * Completing a document, asked once (#2132, wave 4).
 *
 * Purchase order, inquiry, demand and invoice all end the same way, so they ask
 * the same way: the question, the points that have to be taken note of first
 * (see useCompletionWarnings) and a remark that goes into the document's
 * history next to the step it explains.
 *
 * The wording used to change when there were points to tick ("… bestätige die
 * unten stehende Checkbox"). That sentence is gone: the boxes sit right below
 * the question and the button stays greyed out until they are ticked, so the
 * text would only repeat what the dialog already shows.
 */
export const PaloCompleteDocumentDialog = ({
    open,
    onClose,
    onConfirm,
    titleId,
    contentMessageId,
    resourceNumber,
    confirmId,
    taxes,
    invoiceLocation,
    documentType,
    documentId,
}: PaloCompleteDocumentDialogProps): ReactElement => {
    const warnings = useCompletionWarnings({ taxes, invoiceLocation });

    return (
        <PaloStatusChangeDialog
            open={open}
            onClose={onClose}
            title={<FormattedMessage id={titleId} />}
            description={<PoolFormattedMessage id={contentMessageId} values={{ number: resourceNumber }} />}
            confirmLabel={<FormattedMessage id={confirmId ?? 'app.common.complete'} />}
            acknowledgements={warnings.map(id => (
                <PoolFormattedMessage key={id} id={id} />
            ))}
            documentType={documentType}
            documentId={documentId}
            onConfirm={onConfirm}
        />
    );
};
